import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const NOTIFICATIONS_COLLECTION = 'notifications';
const CONFIG_DOC_ID = 'config';
const IMMEDIATE_DOC_ID = 'immediate';

export interface NotificationConfig {
  enabled: boolean;
  intervalHours: number;
  messages: string[];
}

export interface ImmediateNotification {
  message: string;
  timestamp: string;
  sent: boolean;
}

/**
 * Get notification configuration from Firestore
 */
export const getNotificationConfig = async (): Promise<NotificationConfig | null> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, CONFIG_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        enabled: data.enabled ?? true,
        intervalHours: data.intervalHours ?? 24,
        messages: data.messages ?? []
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting notification config:', error);
    throw error;
  }
};

/**
 * Save notification configuration to Firestore
 */
export const saveNotificationConfig = async (config: NotificationConfig): Promise<void> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, CONFIG_DOC_ID);
    await setDoc(docRef, {
      enabled: config.enabled,
      intervalHours: config.intervalHours,
      messages: config.messages
    });
  } catch (error) {
    console.error('Error saving notification config:', error);
    throw error;
  }
};

/**
 * Send an immediate notification to all devices
 * This creates a document that Unity clients will poll for
 */
export const sendImmediateNotification = async (message: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, IMMEDIATE_DOC_ID);
    const timestamp = new Date().toISOString();
    
    await setDoc(docRef, {
      message,
      timestamp,
      sent: true
    });

    console.log('Immediate notification created:', { message, timestamp });
  } catch (error) {
    console.error('Error sending immediate notification:', error);
    throw error;
  }
};

/**
 * Clear immediate notification (optional cleanup)
 */
export const clearImmediateNotification = async (): Promise<void> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, IMMEDIATE_DOC_ID);
    await setDoc(docRef, {
      message: '',
      timestamp: new Date().toISOString(),
      sent: false
    });
  } catch (error) {
    console.error('Error clearing immediate notification:', error);
    throw error;
  }
};
